// CmdInterpreter.js
/**
 * @fileoverview CmdInterpreter validates and interprets commands returned by agent API.
 */

// Extend base eslint camelcase allowances with Django-style filter lookups: `<field>_i`
// matches <field> case-insensitively, alongside plain `<field>` for an exact match.
/* eslint camelcase: ["error", { properties: "never", allow: [
    "^[a-zA-Z0-9]+_[A-Z0-9][a-zA-Z0-9]*$",
    "^dd?Gs?_(bound|LP?|PL?)$",
    "^[a-z][a-zA-Z0-9]*_i$"
]}] */

import { UserActions } from 'BMapsCmds';
import { BackgroundColors } from 'BMapsSrc/themes';
import { App } from 'BMapsSrc/BMapsApp';
import { parseResidueSpec } from 'BMapsUtil/mol_info_utils';
import { ensureArray, conditionalArrayItem, deepFreeze } from 'BMapsUtil/js_utils';
import {
    Compound, AtomGroup, AtomGroupTypes, canStyleAtomGroup,
} from 'BMapsModel';
import { HydrogenDisplayOptions } from 'BMapsUtil/display_utils';
import { HotspotThresholdRange, InspectorTabs, WaterStates } from 'BMapsModel/DisplayState';
import { EventBroker } from 'BMapsSrc/eventbroker';
import { getCompoundForAlias, formatLabels } from './cmds_helpers';
import { interpretActionPlan as dispatchActionPlan } from './gibbsDispatch';

// Validation information
// Exported read-only (it is deepFrozen) so tests can assert that a command is actually reachable
// from this interface, rather than only that its UserCmd exists.
export const knownCommands = deepFreeze({
    SetView: {
        title: 'Set View',
        argInfo: [{
            options: ['protein', 'ligand', 'publication'],
        }],
        description: 'Switch to ligand, protein, or publication view.',
    },
    ResetView: {
        title: 'Reset View',
        argInfo: [],
        description: 'Reset display settings and return the camera to the default whole-protein view.',
    },
    SetDisplayStyle: {
        title: 'Display Style',
        argInfo: [{
            options: ['default', 'wireframe', 'sticks', 'ballandstick', 'spacefill', 'cartoon', 'surface'],
        }],
        description: 'Set the global display style (wireframe, sticks, spacefill, cartoon, etc.).',
    },
    SetHotspots: {
        title: 'Hotspots',
        argInfo: [{
            type: 'boolean',
        }],
        description: 'Show or hide hotspots.',
    },
    SetHydrogenView: {
        title: 'Hydrogen Display',
        argInfo: [{
            options: Object.values(HydrogenDisplayOptions),
        }],
        description: 'Show all, only polar, or no hydrogen atoms.',
    },
    SetWaterView: {
        title: 'Water Display',
        argInfo: [{
            options: Object.values(WaterStates),
        }],
        description: 'Show or hide crystal and/or computed water molecules.',
    },
    SetPiPiStacking: {
        title: 'Pi-Pi Stacking',
        argInfo: [{ type: 'boolean' }],
        description: 'Show or hide pi-pi stacking interactions.',
    },
    SetLigandSolvation: {
        title: 'Ligand Desolvation',
        argInfo: [{ type: 'boolean' }],
        description: 'Show or hide ligand desolvation.',
    },
    ChangeHotspotThreshold: {
        title: 'Hotspot Threshold',
        argInfo: [{ type: 'number' }],
        execute: async (value) => {
            const { min, max } = HotspotThresholdRange;
            const clamped = Math.min(max, Math.max(min, value));
            return UserActions.ChangeHotspotThreshold(clamped);
        },
        description: 'Set the energy threshold for hotspot display.',
    },
    StepBindingSiteRadius: {
        title: 'Binding Site Radius',
        argInfo: [{ type: 'number' }],
        description: 'Increase or decrease the binding site display radius.',
    },
    // The camera moves take an options object, while this interface passes positional arguments,
    // so each one adapts rather than falling through to UserActions[action](...args).
    RotateView: {
        title: 'Rotate View',
        // The axis is optional: validateCommand treats an argInfo entry as REQUIRED unless it
        // says otherwise, so without this a two-word "rotate 90" is rejected for arity before it
        // ever reaches execute. rotateView defaults the axis to 'y' when it arrives undefined.
        argInfo: [{ type: 'number' }, { options: ['x', 'y', 'z'], optional: true }],
        execute: async (degrees, axis) => UserActions.RotateView(degrees, { axis }),
        description: 'Rotate the camera about the molecule by a number of degrees.',
    },
    ZoomView: {
        title: 'Zoom View',
        // Optional, matching zoomView's own `factor = 2` default: an omitted factor should zoom
        // by the standard step, not be refused for arity.
        argInfo: [{ type: 'number', optional: true }],
        // > 1 zooms in, between 0 and 1 zooms out -- 3Dmol's own convention.
        execute: async (factor) => UserActions.ZoomView({ factor }),
        description: 'Zoom the camera in (factor above 1) or out (factor below 1).',
    },
    PanView: {
        title: 'Pan View',
        // Only x is required; y and z match panView's own `= 0` defaults, so "pan left 5" can
        // arrive as a single displacement rather than needing two explicit zeroes.
        argInfo: [
            { type: 'number' },
            { type: 'number', optional: true },
            { type: 'number', optional: true },
        ],
        execute: async (x, y, z) => UserActions.PanView({ x, y, z }),
        description: 'Shift the view along the molecule\'s x, y and z axes, in Angstroms.',
    },
    RecenterView: {
        title: 'Recenter View',
        argInfo: [],
        description: 'Re-frame the view on what is currently displayed.',
    },
    SetColorScheme: {
        title: 'Background Color',
        argInfo: [{
            options: ['default', ...Object.keys(BackgroundColors)],
        }],
        description: 'Set the background color.',
    },
    Select: {
        title: 'Select',
        argInfo: [{
            type: 'object',
        }],
        execute: async (entityRef={}) => {
            const { selectString, resSpec, cmpdIndex } = entityRef;
            let sel = selectString || resSpec || '';
            if (!sel) {
                if (cmpdIndex != null) {
                    const cmpd = getCompoundForAlias(cmpdIndex);
                    sel = cmpd?.selectQuery || '';
                }
                if (!sel) {
                    console.warn(`Unable to form a select query for ${JSON.stringify(entityRef)}`);
                }
            }
            return UserActions.Select(sel);
        },
        description: 'Select an entity in the workspace.',
    },
    OpenImport: {
        title: 'Add Compound',
        argInfo: [
            { type: 'string', optional: true },
            { type: 'object', optional: true },
        ],
        execute: async (tabId = 'import_dragpaste_tab', entityRef) => {
            EventBroker.publish('escapeKey');
            const args = {};
            if (entityRef) {
                const [cmpd] = findAtomGroups(entityRef);
                if (cmpd instanceof Compound) {
                    args.selectedCompounds = [cmpd];
                } else {
                    console.warn(`Non-compound ${cmpd?.resSpec} passed to OpenImport`);
                }
            }
            return UserActions.OpenImport(tabId, Object.keys(args).length ? args : undefined);
        },
        description: 'Open the add compound panel.',
        labelMap: {
            import_dragpaste_tab: 'Enter Mol. Data',
            import_cddv_tab: 'CDD Vault',
            compound_generation_tab: 'Compound Gen.AI',
            jobs_compounds_tab: 'Compound Jobs',
            jobs_compounds_results_tab: 'Compound Job Results',
        },
    },
    OpenProteinImport: {
        title: 'Add Protein',
        argInfo: [{ type: 'string', optional: true }],
        execute: async (tabId = 'protein_search_tab') => {
            EventBroker.publish('escapeKey');
            return UserActions.OpenProteinImport(tabId);
        },
        description: 'Open the add protein panel.',
        labelMap: {
            protein_search_tab: 'Structure Search',
            protein_folding_tab: 'Protein Folding',
            import_protein_tab: 'Upload Protein Data',
            jobs_structures_tab: 'Folding Job Results',
            import_pdb_tab: 'Import from PDB',
            import_alphafold_tab: 'Import from AlphaFold',
            project_targets_tab: 'Project / Targets',
            solute_selector_tab: 'Solute selector',
        },
    },
    OpenScreeningPanel: {
        title: 'Screening',
        argInfo: [{ type: 'string', optional: true }],
        execute: async (tabId = 'new_screen_tab') => UserActions.OpenScreeningPanel(tabId),
        description: 'Open the screening panel.',
        labelMap: {
            new_screen_tab: 'New Screen',
            screen_jobs_tab: 'Screening Jobs',
            screen_results_tab: 'Screening Results',
        },
    },
    ClosePane: {
        title: 'Close Dialog',
        argInfo: [],
        execute: async () => {
            EventBroker.publish('escapeKey');
        },
        description: 'Close the currently open import dialog.',
    },
    CloseSidePanel: {
        title: 'Close Panel',
        argInfo: [{ type: 'string', optional: true }],
        execute: async (positionId='left') => {
            UserActions.CloseSidePanel(positionId);
        },
        description: 'Close a side panel (compound table, scoring table, help, etc.).',
    },
    OpenHelp: {
        title: 'Help',
        argInfo: [],
        execute: async () => UserActions.OpenSidePanel('right', { pageId: 'help' }),
        description: 'Open the help panel.',
    },
    OpenCommandPanel: {
        title: 'Commands Panel',
        argInfo: [],
        execute: async () => UserActions.OpenSidePanel('right', { pageId: 'commandPanel' }),
        description: 'Open the command panel.',
    },
    OpenCompoundsPanel: {
        title: 'Compound Table',
        argInfo: [],
        description: 'Open the compound table.',
    },
    OpenScoringPanel: {
        title: 'Scoring Table',
        argInfo: [],
        description: 'Open the scoring table.',
    },
    ShowInspectorTab: {
        title: 'Inspector Tab',
        argInfo: [{
            options: Object.values(InspectorTabs),
        }],
        // Asking for a tab here is asking to see it, so show a hidden Inspector too.
        execute: async (tab) => UserActions.ShowInspectorTab(tab, { ensureVisible: true }),
        description: 'Open a specific tab in the inspector panel.',
    },
    ExpandInfoDisplayPanel: {
        title: 'Show Selector / Inspector',
        argInfo: [{ options: ['selector', 'inspector'] }],
        description: 'Show (expand) the Selector or Inspector panel.',
    },
    CollapseInfoDisplayPanel: {
        title: 'Hide Selector / Inspector',
        argInfo: [{ options: ['selector', 'inspector'] }],
        description: 'Hide (collapse) the Selector or Inspector panel, leaving its icon.',
    },
    OpenDock: {
        title: 'Docking',
        argInfo: [{ type: 'array', optional: true }],
        execute: async (compoundRefs = []) => {
            if (compoundRefs.length === 0) {
                return UserActions.OpenDock();
            } else {
                const compounds = findAtomGroups(compoundRefs);
                return UserActions.OpenDock(compounds);
            }
        },
        description: 'Open the docking panel, optionally with specific compounds.',
    },
    OpenSketcher: {
        title: 'Sketcher',
        argInfo: [{ type: 'object', optional: true }],
        execute: async (compoundRef) => {
            if (!compoundRef) {
                return UserActions.OpenSketcher();
            }
            const [compound] = findAtomGroups(compoundRef);
            return UserActions.OpenSketcher(compound);
        },
        description: 'Open the 2D structure sketcher, optionally for a specific compound.',
    },
    ExportCompound: {
        title: 'Export',
        argInfo: [{ type: 'array', optional: true }],
        execute: async (compoundRefs = []) => {
            if (compoundRefs.length === 0) {
                return UserActions.OpenExport();
            } else {
                const compounds = findAtomGroups(compoundRefs);
                return UserActions.OpenExport({ compounds });
            }
        },
        description: 'Export compounds to a file.',
    },
    ActivateNextCompound: {
        title: 'Next / Previous Compound',
        argInfo: [{
            type: 'number',
        }],
        description: 'Step to the next or previous compound in the list.',
    },
    PinCompound: {
        title: 'Pin Compound',
        argInfo: [{ type: 'object' }, { type: 'boolean' }],
        execute: async (entityRef, shouldPin) => {
            const [cmpd] = findAtomGroups(entityRef);
            if (!cmpd) {
                console.log(`No compound found for entityRef: ${JSON.stringify(entityRef)}`);
                return null;
            }
            console.log('PinCompound calls on:', cmpd, `with pin: ${shouldPin}`);
            return UserActions.ToggleVisibility(
                cmpd,
                shouldPin,
            );
        },
        description: 'Pin or unpin a compound.',
    },
    StarCompound: {
        title: 'Star Compound',
        argInfo: [{ type: 'object' }, { type: 'boolean' }],
        execute: async (entityRef, shouldStar) => {
            const [cmpd] = findAtomGroups(entityRef);
            if (!cmpd) {
                console.log(`No compound found for entityRef: ${JSON.stringify(entityRef)}`);
                return null;
            }
            console.log('StarCompound calls on:', cmpd, `with star: ${shouldStar}`);
            return UserActions.ToggleStarred(
                cmpd,
                shouldStar,
            );
        },
        description: 'Star or unstar a compound.',
    },
    EnergyMinimize: {
        title: 'Energy Minimize',
        argInfo: [{ type: [{ type: 'object' }], optional: true }],
        execute: async (input) => {
            // No arg case is like clicking "Energy Minimize" in the Right Control Buttons
            if (!input || (input.length != null && input.length === 0)) {
                return UserActions.RequestMinimize();
            }

            // Specifically minimizing one or more compounds
            const compounds = findAtomGroups(input);
            return UserActions.EnergyMinimize(compounds);
        },
        description: 'Run energy minimization on specified compounds, or the active compound if none given.',
    },
    SetFocusCompound: {
        title: 'Focus Compound',
        argInfo: [{ type: 'object' }],
        execute: async (entityRef) => {
            const [cmpd] = findAtomGroups(entityRef);
            if (!cmpd) {
                console.log(`No compound matching ${JSON.stringify(entityRef)}`);
                return;
            }
            App.Workspace.activateCompound(cmpd);
        },
        description: 'Set the active / focus compound.',
    },
    SetCustomAtomGroupStyle: {
        title: 'Set Atom Style',
        argInfo: [{ type: 'object' }, { type: 'object' }],
        execute: async (entityRef, styleObj) => {
            const atomGroups = findAtomGroups(entityRef);
            if (atomGroups.length === 0) {
                console.log(`No atom groups matching ${JSON.stringify(entityRef)}`);
                return undefined;
            }
            // Only groups whose style is drawn (StyleableAtomGroupTypes); asking for waters alone
            // is reported, not silently accepted.
            const styleable = atomGroups.filter(canStyleAtomGroup);
            if (styleable.length === 0) {
                const types = [...new Set(atomGroups.map(({ type }) => type))].join(', ');
                return { error: `Styling ${types} groups is not supported yet.` };
            }
            styleable.forEach((group) => UserActions.SetCustomAtomGroupStyle(group, styleObj));
            UserActions.RedrawScene();
            return undefined;
        },
        description: 'Set the display style for a specific atom, residue, or compound.',
    },
    RenameCompound: {
        title: 'Rename Compound',
        argInfo: [{ type: 'object' }],
        execute: async (entityRef) => {
            const [cmpd] = findAtomGroups(entityRef);
            if (!cmpd) {
                return { error: 'Compound not found for renaming' };
            } else if (!(cmpd instanceof Compound)) {
                return { error: `${cmpd?.resSpec || 'The entity'} is not a compound` };
            } else if (cmpd.isLigand()) {
                return { error: 'Crystal ligands cannot be renamed' };
            }
            return UserActions.OpenRenameCompound(cmpd);
        },
        description: 'Rename a compound. Crystal ligands cannot be renamed.',
    },
    CopyCompound: {
        title: 'Copy / Duplicate Compound',
        argInfo: [{ type: 'object' }],
        execute: async (entityRef) => {
            const disallowedTypes = [
                AtomGroupTypes.ComputedWater, AtomGroupTypes.Fragment, AtomGroupTypes.CrystalWater,
            ];
            const atomGroups = findAtomGroups(entityRef)
                .flatMap((ag) => (ag.getResidues ? ag.getResidues() : ag))
                .filter((ag) => !disallowedTypes.includes(ag.type));
            if (atomGroups.length === 0) {
                console.log(`No atom groups matching ${JSON.stringify(entityRef)} for CopyCompound`);
                return { compounds: [], errors: ["Couldn't find the compound/residues to copy"] };
            }
            if (atomGroups.length === 1 && atomGroups[0] instanceof Compound) {
                return UserActions.CopyCompound(atomGroups[0]);
            } else {
                return UserActions.CopyResiduesToCompound(atomGroups);
            }
        },
        description: 'Copy a compound, or create a new compound from residues.',
    },
    GroupItems: {
        title: 'Group Compounds',
        argInfo: [
            { type: 'array' },
            { type: 'string', optional: true },
        ],
        execute: async (compoundRefs, groupName) => {
            const compounds = findAtomGroups(compoundRefs);
            if (compounds.length === 0) {
                return { error: 'No compounds found to group' };
            }
            const treeItems = App.Workspace.getTreeItemsForItems('Compounds', compounds);
            if (treeItems.length === 0) {
                return { error: 'No tree items found for the specified compounds' };
            }
            const groupInfo = groupName ? { groupName } : undefined;
            return UserActions.GroupItems(treeItems, 'Compounds', undefined, groupInfo);
        },
        description: 'Group compounds in the compound tree, with an optional group name.',
    },
    ListCommands: {
        title: 'List Available Commands',
        argInfo: [
            { type: 'array', optional: true },
        ],
        execute: () => null,
        description: 'List available commands and their descriptions.',
    },
});

function getMappedLabel(action, value) {
    return knownCommands[action]?.labelMap?.[value] || value;
}

/**
 * Find atom groups meeting the provided residue specification.
 * @param {object | Array<object>} filters - an object containing filtering properties
 * @param {string} [filters.resSpec] - Residue specification string (e.g., "GLU.217:A").
 * @param {string} [filters.resn] - Residue name, matched exactly (e.g., 'GLU').
 * @param {string} [filters.resn_i] - Residue name, matched case-insensitively (e.g., 'glu').
 * @param {number} [filters.resi] - Residue sequence number (e.g., 217).
 * @param {string} [filters.chain] - Chain ID (e.g., 'A').
 * @param {string} [filters.insCode] - Insertion code.
 * @param {string} [filters.altLoc] - Alternate location.
 * @param {number} [filters.modelNum] - Model number.
 * @param {string} [filters.cmpdIndex] - Compound label (e.g., "A", "B", "AA").
 * @returns {Array<object>} An array of matching atom group objects.
 */
export function findAtomGroups(filters) {
    if (Array.isArray(filters)) {
        return filters.flatMap((item) => {
            const res = findAtomGroups(item);
            if (res.length === 0) {
                console.warn(`CmdInterpreter.findAtomGroups: nothing found for ${JSON.stringify(item)}`);
            }
            return res;
        });
    }

    const {
        resSpec, resn, resn_i, resi, chain, insCode, altLoc, modelNum, cmpdIndex,
    } = filters;
    console.log('find atomgroups called with:', {
        resSpec, resn, resn_i, resi, chain, insCode, altLoc, modelNum, cmpdIndex,
    });
    if (resSpec) {
        const parsedSpec = parseResidueSpec(resSpec);
        if (parsedSpec) {
            // Match a resSpec as bioleap does (ResidueID::Match in bmoc/ratoms.elp): the
            // residue name ignores case, the chain does not. Change the two together, or the
            // same spec resolves differently here and on the server.
            const { resn: specResn, ...rest } = parsedSpec;
            return findAtomGroups({ ...rest, resn_i: specResn });
        } else {
            console.warn(`Could not parse residue spec: "${resSpec}"`);
        }
    }
    if (cmpdIndex != null) {
        const compound = getCompoundForAlias(cmpdIndex);
        if (!compound) {
            console.warn(`No compound found for label ${cmpdIndex}`);
            return [];
        }
        return [compound];
    }

    // Here we should have resn or resn_i, resi, chain, insCode, altLoc, modelNum
    // At least one of the spec components must be provided.
    const anyResn = resn || resn_i;
    const emptySpec = !anyResn && resi == null && !chain && !insCode && !altLoc && modelNum == null;
    if (emptySpec) {
        return [];
    }

    const resnUpper = resn_i ? resn_i.toUpperCase() : null;
    let found = App.Workspace.allCaseData().flatMap((cd) => cd.allAtomGroups()).filter((ag) => (
        (!resn || ag.resn === resn)
        && (!resnUpper || ag.resn?.toUpperCase() === resnUpper)
        && (resi == null || ag.resi === resi)
        && (!chain || ag.chain === chain)
        && (!insCode || ag.insCode === insCode)
        && (!altLoc || ag.altLoc === altLoc)
        && (modelNum == null || ag.modelNum === modelNum)
    ));

    // The resSpec of chain groups (Protein, Polymer) match the first residue, so:
    //   1. If only chain is requested, and there is a chain group, return only the chain group.
    //   2. If not only chain, exclude chain groups from the results.
    // modelNum is omitted: a chain in a given model is still a chain-only query.
    const onlyChainQuery = chain && !anyResn && resi == null && !insCode && !altLoc;
    const anyChainGroups = found.some((ag) => AtomGroup.ChainTypes.includes(ag.type));
    found = found.filter((ag) => (
        // If query is just chain (eg :X) and there is a chain type, return just the chain group
        (onlyChainQuery && (!anyChainGroups || AtomGroup.ChainTypes.includes(ag.type)))
        // If query is more specific (eg 23:X), exclude chain group types (preferring a residue)
        || (!onlyChainQuery && !AtomGroup.ChainTypes.includes(ag.type))
    ));

    return found;
}

function getCommandLabel(action, args) {
    function emptyArg(arr) {
        return !arr[0] || arr[0].length === 0;
    }
    switch (action) {
        case 'SetView': return `Set the view to ${args[0]}`;
        case 'ResetView': return 'Reset view';
        case 'SetDisplayStyle': return `Set display style to ${args[0]}`;
        case 'SetHotspots': return `Set hotspots ${args[0] ? 'On' : 'Off'}`;
        case 'SetHydrogenView': return `Set hydrogen display to ${args[0]}`;
        case 'SetWaterView': return `Set water display to ${args[0]}`;
        case 'SetPiPiStacking': return `Set pi-pi stacking ${args[0] ? 'On' : 'Off'}`;
        case 'SetLigandSolvation': return `Set ligand desolvation ${args[0] ? 'On' : 'Off'}`;

        case 'ChangeHotspotThreshold': return `Set hotspot threshold to ${args[0]}`;
        case 'StepBindingSiteRadius': return `Step binding site radius by ${args[0]}`;
        case 'RotateView': return `Rotate view ${args[0]} degrees about ${args[1] ?? 'y'}`;
        case 'ZoomView': return `Zoom view by ${args[0]}`;
        case 'PanView': return `Pan view by (${args[0] ?? 0}, ${args[1] ?? 0}, ${args[2] ?? 0})`;
        case 'RecenterView': return 'Recenter the view';
        case 'SetColorScheme': return `Set background color ${args[0]}`;
        case 'Select': {
            const [spec] = args;
            const label = formatLabels(spec);
            return `Select ${label}`;
        }
        case 'RequestMinimize': return 'Minimize compound';
        case 'PinCompound': {
            const [entityRef, shouldPin] = args;
            return `${shouldPin ? 'Pin' : 'Unpin'} ${formatLabels(entityRef)}`;
        }
        case 'StarCompound': {
            const [entityRef, shouldStar] = args;
            return `${shouldStar ? 'Star' : 'Unstar'} ${formatLabels(entityRef)}`;
        }
        case 'EnergyMinimize': {
            const argArray = ensureArray(args);
            if (emptyArg(argArray)) {
                const focusCmpd = App.Workspace.getActiveCompound();
                const label = focusCmpd ? focusCmpd.displayName : 'no focus compound';
                return `Minimize focus compound (${label})`;
            }
            const label = argArray.map(formatLabels).join(', ');
            return `Minimize ${label}`;
        }
        case 'SetFocusCompound': {
            const [spec] = args;
            const label = formatLabels(spec);
            return `Focus on ${label}`;
        }
        case 'ActivateNextCompound': {
            const step = args[0];
            return `Step focus compound ${step < 0 ? step : `+${step}`}`;
        }
        case 'SetCustomAtomGroupStyle': {
            const [spec, style] = args;
            const label = formatLabels(spec);
            const parts = Object.entries(style).map(([key, value]) => {
                switch (key) {
                    case 'molStyle': return `molecular style ${value}`;
                    case 'color': return `color ${value}`;
                    case 'colorAllAtoms': return `color all atoms ${value ? 'on' : 'off'}`;
                    case 'showLabel': return `label ${value ? 'on' : 'off'}`;
                    default: return '';
                }
            }).filter(Boolean);
            return `Style ${label}: ${parts.join(', ')}`;
        }
        case 'OpenScreeningPanel': return args[0]
            ? `Open screening panel (${getMappedLabel(action, args[0])})`
            : 'Open screening panel';
        case 'OpenCompoundsPanel': return 'Open compound table';
        case 'OpenScoringPanel': return 'Open scoring table';
        case 'ShowInspectorTab': return `Open inspector: ${args[0]}`;
        case 'ExpandInfoDisplayPanel': return `Show ${args[0]}`;
        case 'CollapseInfoDisplayPanel': return `Hide ${args[0]}`;
        case 'OpenSketcher': return args[0] ? `Open sketcher for ${formatLabels(args[0])}` : 'Open sketcher';
        case 'OpenHelp': return 'Open help panel';
        case 'OpenCommandPanel': return 'Open command panel';
        case 'ClosePane': return 'Close pane';
        case 'CloseSidePanel': return `Close ${args[0] || 'left'} panel`;
        case 'GroupItems': return args[1] ? `Group items as "${args[1]}"` : 'Group items';
        case 'OpenImport': {
            const tabId = args[0];
            const compoundRef = args[1];
            const tabLabel = tabId ? ` (${getMappedLabel(action, tabId)})` : '';
            if (compoundRef) return `Open Compound Import${tabLabel} for ${formatLabels(compoundRef)}`;
            return `Open Compound Import${tabLabel}`;
        }
        case 'OpenProteinImport': return args[0]
            ? `Open Add Protein (${getMappedLabel(action, args[0])})`
            : 'Open Add Protein';
        case 'OpenDock': {
            if (emptyArg(args)) {
                return 'Open the docking panel';
            }
            const compounds = formatLabels(args[0]);
            return `Dock ${compounds}`;
        }
        case 'ExportCompound': {
            if (emptyArg(args)) {
                return 'Export';
            }
            const compounds = formatLabels(args[0]);
            return `Export ${compounds}`;
        }
        case 'CopyCompound': {
            return `Copy ${formatLabels(args[0])}`;
        }
        case 'RenameCompound': {
            return `Rename ${formatLabels(args[0])}`;
        }
        case 'ListCommands': {
            const commands = args[0];
            const entries = Array.isArray(commands) && commands.length > 0
                ? commands.map((cmd) => [cmd, knownCommands[cmd]])
                : Object.entries(knownCommands);
            return entries
                .map(([name, def]) => `• ${def?.title || name}: ${def?.description || ''}`)
                .join('\n');
        }
        default: return action;
    }
}

export default class CmdInterpreter {
    // Gibbs integration seam

    /**
     * Dispatch an ActionDef-based action plan from the Gibbs text-to-action pipeline. The
     * dispatcher lives in gibbsDispatch.js (the registered seam of the Gibbs import boundary);
     * this forwarder keeps the name every caller and the fork's tests use, and keeps it
     * patchable on the class.
     *
     * @param {Object} plan - ActionPlan from textToAction()
     * @returns {Promise<Object>} Execution result
     */
    static interpretActionPlan(plan, opts) {
        return dispatchActionPlan(plan, opts);
    }

    // Legacy (Non-Gibbs) command handling

    /**
     * Apply a command array returned by agent API.
     * This validates and executes each command representation.
     * @param {Array<[string, ...]>} cmdArray
     * @returns {Promise<{
     *     results: [{ commandUnit, result, errors, displayText}],
     *     error: string?
     * }>}
     */
    static async interpretCommand(cmdArray) {
        const results = { results: [], error: null };
        if (!Array.isArray(cmdArray) || cmdArray.length === 0) {
            results.error = 'Could not interpret command from words';
            return results;
        }
        console.log(`Applying interpreted command from words: ${JSON.stringify(cmdArray)}`);
        for (const unit of cmdArray) {
            const { action, args, error } = CmdInterpreter.validateCommand(unit);
            if (error) {
                const msg = `Validation error: ${error}`;
                console.log(msg);
                results.results.push({
                    commandUnit: unit,
                    errors: [msg],
                    displayText: CmdInterpreter.printCommandInfo(unit),
                });
                continue;
            }
            const cmdDef = knownCommands[action];
            let result;
            const errors = [];
            try {
                if (cmdDef.execute) {
                    result = await cmdDef.execute(...args);
                } else {
                    result = await UserActions[action](...args);
                }
                if (result?.error) errors.push(result?.error);
                if (result?.errors) errors.push(...result?.errors);
            } catch (err) {
                // A command that THROWS should report like one that returns an error, for two
                // reasons. It reaches the user: these results are published to the command console,
                // whereas an escaping exception is an unhandled rejection that shows nothing --
                // applyWordsAsCommands is called without await or catch from both the typed and
                // voice entry points. And it keeps the batch going: without this, one refusal
                // abandons every later command in the same array.
                console.warn(`Command ${action} failed:`, err);
                errors.push(err?.message || String(err));
            }

            results.results.push({
                commandUnit: unit,
                result,
                errors,
                displayText: CmdInterpreter.printCommandInfo(unit, errors),
            });
        }
        return results;
    }

    /**
     * Validates a command action and arguments, returning them if valid.
     * @param {[string, ...]} cmdUnit
     * @returns {{ action: string, args: [] } | { error: string }}
     */
    static validateCommand(cmdUnit) {
        if (!Array.isArray(cmdUnit) || cmdUnit.length === 0) {
            return { error: 'Invalid command' };
        }
        const [action, ...args] = cmdUnit;
        const { argInfo } = knownCommands[action] || {};
        if (!argInfo) {
            return { error: `Invalid action ${action}` };
        }
        // Optional arguments
        const requiredArgs = argInfo.filter((info) => !info.optional);
        const totalArgs = argInfo.length;
        if (args.length < requiredArgs.length || args.length > totalArgs) {
            const expectedDesc = requiredArgs.length === totalArgs
                ? `${totalArgs}`
                : `${requiredArgs.length}-${totalArgs}`;
            return { error: `Invalid number of arguments for ${action}: ${args.length} provided, ${expectedDesc} expected` };
        }
        // In case of missing optional arguments
        const paddedArgs = [...args];
        while (paddedArgs.length < argInfo.length) {
            paddedArgs.push(undefined);
        }
        for (let i = 0; i < paddedArgs.length; i++) {
            if (paddedArgs[i] !== undefined || !argInfo[i].optional) {
                const error = CmdInterpreter.validateArg(paddedArgs[i], argInfo[i]);
                if (error) return { error: `Invalid argument ${i} for ${action}: ${error}` };
            }
        }
        return { action, args: paddedArgs };
    }

    /**
     * Validate a single command argument
     * @param {any} arg
     * @param {{ type: string?, options: []?}} argInfo
     * @returns {string?} error string or null if successful
     */
    static validateArg(arg, argInfo) {
        const { type, options, optional } = argInfo;
        // Handle optional arguments that are undefined
        if (arg === undefined && optional) {
            return null;
        }

        if (Array.isArray(type)) {
            if (type.some((argInfo2) => !CmdInterpreter.validateArg(arg, argInfo2))) {
                // If any of the potential arg types returns falsy, it passes the validation
                return null;
            } else {
                return `Provided type ${typeof arg} doesn't match allowed list of types`;
            }
        }

        if (type) {
            const provided = typeof arg;
            switch (type) {
                case 'string':
                    // Note: eslint enforces always comparing typeof results to string literals
                    if (provided !== 'string') return `${type} required, ${provided} provided`;
                    break;
                case 'boolean':
                    if (provided !== 'boolean') return `${type} required, ${provided} provided`;
                    break;
                case 'object':
                    if (provided !== 'object' || arg == null) return `${type} required, ${provided} provided`;
                    break;
                case 'number':
                    if (provided !== 'number') return `${type} required, ${provided} provided`;
                    break;
                case 'array':
                    if (!Array.isArray(arg)) return `${type} required, ${provided} provided`;
                    break;
                // no default
            }
        }
        if (options) {
            if (!options.includes(arg)) return 'invalid option';
        }
        return null;
    }

    static printCommandInfo(cmdUnit, errors=[]) {
        if (!Array.isArray(cmdUnit) || cmdUnit.length === 0 || !knownCommands[cmdUnit[0]]) {
            return 'Invalid command';
        }
        const [action0, ...args0] = cmdUnit;
        const { action, args, error: validationError } = CmdInterpreter.validateCommand(cmdUnit);
        const label = getCommandLabel(action || action0, args || args0);

        const errMsg = [
            ...conditionalArrayItem(validationError || errors?.length > 0, 'failed'),
            ...conditionalArrayItem(validationError, 'invalid parameters'),
        ].join(', ');
        return errMsg ? `${label} (${errMsg})` : label;
    }
}
